import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineSearch, HiArrowRight } from 'react-icons/hi';
import { useLanguage } from '../../contexts/LanguageContext';
import { searchProducts, getProductsBySymptom } from '../../services/apiService';
import { getOptimizedImageUrl, normalizeImageArray } from '../../utils/imageUtils';
import { scrollToTop } from '../../utils/scrollUtils';
import { trackEvent, setClickSource } from '../../services/analytics/analyticsService';
import PriceDisplay from './PriceDisplay';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 150;
const PREVIEW_COUNT = 5;
// The catalog stores one record per pack size of the same family, and the
// backend drops those duplicates (and Rx-only records) after fetching. Asking
// for exactly PREVIEW_COUNT then left the panel with 2 items for "paracetamol",
// so fetch a wider page and trim it here.
const PREVIEW_FETCH = 20;

/**
 * The always-visible search field in the desktop nav, replacing the icon that
 * used to open the full-screen search panel. Same query behaviour as that panel
 * (debounced preview, symptom shortcut, did-you-mean) — it just lives inline and
 * drops its results below the field instead of over the page.
 *
 * The panel is still what mobile uses, so this is rendered only from `custom:`
 * up and the two never appear at once.
 */
const NavSearchBar = React.memo(() => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [didYouMean, setDidYouMean] = useState(null);
  const [matchedSymptom, setMatchedSymptom] = useState(null);
  const rootRef = useRef(null);

  // Close on outside click. The field itself keeps its text — only the dropdown
  // is dismissed, so clicking away and back does not lose a typed query.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setShowPreview(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length < MIN_QUERY) {
      setResults([]);
      setShowPreview(false);
      setMatchedSymptom(null);
      setDidYouMean(null);
      return undefined;
    }

    setIsLoading(true);
    setShowPreview(true);

    const timer = setTimeout(async () => {
      try {
        const response = await searchProducts(searchTerm, 1, PREVIEW_FETCH, '', 'random', '');
        const ms = response?.success ? response.data.matchedSymptom || null : null;
        setMatchedSymptom(ms);
        if (ms) {
          // A text search for a symptom word matches nothing in the German
          // catalog, so preview the symptom's products instead.
          const symResp = await getProductsBySymptom(ms.key, 1, PREVIEW_FETCH);
          setResults(symResp?.success ? (symResp.data.products || []).slice(0, PREVIEW_COUNT) : []);
          setDidYouMean(null);
        } else if (response?.success && Array.isArray(response.data.products)) {
          setResults(response.data.products.slice(0, PREVIEW_COUNT));
          setDidYouMean(response.data.didYouMean || null);
        }
      } catch {
        setResults([]);
        setDidYouMean(null);
        setMatchedSymptom(null);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const goToResults = () => {
    const q = searchTerm.trim();
    if (!q) return;
    trackEvent('search', { query: q });
    // A matched symptom routes to the symptom listing, mirroring the panel:
    // searching the word itself would return nothing.
    if (matchedSymptom) {
      navigate(`/products?symptom=${matchedSymptom.key}`);
    } else {
      navigate(`/products?${new URLSearchParams({ search: q }).toString()}`);
    }
    setShowPreview(false);
    setTimeout(() => scrollToTop(), 100);
  };

  const openProduct = (pharmacode) => {
    setClickSource('search');
    navigate(`/product/${pharmacode}`);
    setShowPreview(false);
    setSearchTerm('');
  };

  const productTitle = (article) => {
    const folder = article?.consumerFolder;
    const fromFolder = Array.isArray(folder) ? folder[0]?.title : folder?.title;
    return fromFolder || article?.description?.longDescription || '';
  };

  return (
    // Flexible: fills the space the nav row has left, between a usable minimum
    // and the old 36rem maximum, so it never pushes the nav past the viewport.
    <div ref={rootRef} className="relative hidden min-w-[10rem] max-w-[36rem] flex-1 custom:block">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goToResults();
        }}
        role="search"
      >
        <label htmlFor="nav-search-input" className="sr-only">
          {t('nav.buttons.openSearch')}
        </label>
        <div className="flex w-full items-center gap-2 rounded-full border border-white/40 bg-white pl-4 landing:border-nav-ink/15 pr-1.5 py-2 shadow-[0_10px_26px_-14px_rgb(var(--brand-shadow-deep)/0.7)] transition-all duration-200 focus-within:border-white focus-within:ring-4 focus-within:ring-white/30">
          <HiOutlineSearch className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <input
            id="nav-search-input"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.trim().length >= MIN_QUERY && setShowPreview(true)}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowPreview(false); }}
            placeholder={t('searchBar.placeholder')}
            className="min-w-0 flex-1 border-none bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 shadow-none focus:outline-none focus:ring-0"
            style={{ outline: 'none', boxShadow: 'none' }}
            role="combobox"
            aria-expanded={showPreview}
            aria-controls="nav-search-preview"
            aria-autocomplete="list"
            autoComplete="off"
          />
          <button
            type="submit"
            aria-label={t('nav.bottom.search')}
            className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors duration-200 hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1"
          >
            <HiArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </form>

      <AnimatePresence>
        {showPreview && (
          <motion.div
            id="nav-search-preview"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full z-[60] mt-2 w-full min-w-[26rem] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-gray-900/10"
          >
            {matchedSymptom && (
              <button
                type="button"
                onClick={() => { navigate(`/products?symptom=${matchedSymptom.key}`); setShowPreview(false); }}
                className="flex w-full items-center gap-2 border-b border-gray-100 bg-red-50/60 px-4 py-2.5 text-left text-xs font-semibold text-brand hover:bg-red-50"
              >
                {t('searchBar.symptomShortcut')}
              </button>
            )}

            {isLoading ? (
              <div className="px-4 py-6 text-center" role="status" aria-live="polite">
                <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden="true" />
              </div>
            ) : results.length > 0 ? (
              <>
                <ul className="max-h-80 overflow-y-auto py-1">
                  {results.map((product) => {
                    const article = product.articles?.[0] || product;
                    const pharmacode = article.pharmacode || product.pharmacode || product._id;
                    // getOptimizedImageUrl takes an array of image paths, not a
                    // product — and search results carry `images` as a direct
                    // array rather than keyed by size, which is what
                    // normalizeImageArray exists to smooth over.
                    const imageUrl = getOptimizedImageUrl(normalizeImageArray(article, 'F'));
                    return (
                      <li key={product._id || pharmacode}>
                        <button
                          type="button"
                          onClick={() => openProduct(pharmacode)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                        >
                          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt=""
                                aria-hidden="true"
                                className="h-full w-full object-contain"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <svg className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-gray-900">
                              {productTitle(article)}
                            </span>
                            <PriceDisplay article={article} variant="compact" className="mt-0.5" />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  onClick={goToResults}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2.5 text-xs font-semibold text-brand transition-colors hover:bg-gray-50"
                >
                  {t('products.header.allProducts')}
                  <HiArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
              </>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                {/* A did-you-mean is more useful than the empty message, so it
                    replaces it and re-runs the search when picked. */}
                {didYouMean ? (
                  <button
                    type="button"
                    onClick={() => setSearchTerm(didYouMean)}
                    className="font-semibold text-brand hover:underline"
                  >
                    {didYouMean}
                  </button>
                ) : (
                  <span>{t('products.grid.noResultsFound')}</span>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

NavSearchBar.displayName = 'NavSearchBar';

export default NavSearchBar;
